import { useEffect, useRef, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, X } from 'lucide-react';
import type { BackofficeLinkQrCodeSettings } from '../types';

export interface LinkQrCodeSettingsModalProps {
  open: boolean;
  qrCode: BackofficeLinkQrCodeSettings;
  submitting: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (settings: BackofficeLinkQrCodeSettings) => Promise<void> | void;
}

export function LinkQrCodeSettingsModal({
  open,
  qrCode,
  submitting,
  error,
  onClose,
  onSubmit,
}: LinkQrCodeSettingsModalProps) {
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const [foregroundColor, setForegroundColor] = useState(qrCode.foregroundColor);
  const [showLogo, setShowLogo] = useState(qrCode.showLogo);

  useEffect(() => {
    if (!open) {
      return;
    }
    closeRef.current?.focus();
  }, [open]);

  useEffect(() => {
    setForegroundColor(qrCode.foregroundColor);
    setShowLogo(qrCode.showLogo);
  }, [qrCode]);

  if (!open) {
    return null;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await onSubmit({
      foregroundColor,
      showLogo,
    });
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/45 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="link-qr-settings-title"
        className="w-full max-w-md rounded-xl bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between border-b border-neutral-200 px-6 py-5">
          <div>
            <h2
              id="link-qr-settings-title"
              className="font-inter-tight text-xl font-semibold text-neutral-900"
            >
              Edit QR code
            </h2>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-md p-2 text-neutral-500 hover:bg-backoffice-50 hover:text-neutral-900"
          >
            <X className="size-5" />
          </button>
        </div>
        <form className="space-y-4 px-6 py-5" onSubmit={(event) => void handleSubmit(event)}>
          {error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-small font-semibold text-red-700">
              {error}
            </div>
          ) : null}
          <label className="flex flex-col gap-2 text-small font-semibold text-neutral-700">
            QR code color
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={foregroundColor}
                onChange={(event) => setForegroundColor(event.target.value)}
                aria-label="QR code color"
                className="h-10 w-14 rounded-lg border border-neutral-200 bg-white p-1"
              />
              <input
                type="text"
                value={foregroundColor}
                onChange={(event) => setForegroundColor(event.target.value)}
                className="h-10 flex-1 rounded-lg border border-neutral-200 px-3 text-body text-neutral-900"
              />
            </div>
          </label>
          <label className="flex items-center gap-3 rounded-lg border border-neutral-200 px-4 py-3 text-body text-neutral-900">
            <input
              type="checkbox"
              checked={showLogo}
              onChange={(event) => setShowLogo(event.target.checked)}
              className="h-4 w-4 rounded border-neutral-300"
            />
            <span>Show Nava icon</span>
          </label>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-neutral-200 px-4 py-2 text-small font-semibold text-neutral-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center rounded-lg bg-backoffice-600 px-4 py-2 text-small font-semibold text-white disabled:opacity-60"
            >
              {submitting ? (
                <span className="inline-flex items-center whitespace-nowrap">
                  <Loader2 className="mr-1 size-4 animate-spin" />
                  Save
                </span>
              ) : (
                'Save'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
